import { useSyncExternalStore } from "react";
import { apiLogin, apiRegister } from "@/lib/auth-api";

export type LocalAccount = {
  id: string;
  email: string;
  password: string;
  createdAt: string;
  employeeStepDone: boolean;
  onboardingCompleted: boolean;
  /** Login/register lewat API backend + Supabase */
  authProvider?: "local" | "remote";
  accessToken?: string;
  refreshToken?: string;
};

type AuthState = {
  currentUserId: string | null;
  accounts: LocalAccount[];
};

const KEY = "hr-dashboard-auth-v1";

const initial: AuthState = {
  currentUserId: null,
  accounts: [],
};

let state: AuthState = load();
const listeners = new Set<() => void>();

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function normalize(raw: any): AuthState {
  const accounts: LocalAccount[] = Array.isArray(raw?.accounts)
    ? raw.accounts
        .map((account: any) => ({
          id: String(account.id || uid()),
          email: String(account.email || "").trim().toLowerCase(),
          password: String(account.password || ""),
          createdAt: String(account.createdAt || new Date().toISOString()),
          employeeStepDone: !!account.employeeStepDone,
          onboardingCompleted: !!account.onboardingCompleted,
          authProvider: account.authProvider === "remote" ? "remote" : account.authProvider === "local" ? "local" : undefined,
          accessToken: typeof account.accessToken === "string" ? account.accessToken : undefined,
          refreshToken: typeof account.refreshToken === "string" ? account.refreshToken : undefined,
        }))
        .filter((account: LocalAccount) => account.email.length > 0)
    : [];

  return {
    currentUserId: accounts.some((account) => account.id === raw?.currentUserId) ? raw.currentUserId : null,
    accounts,
  };
}

function load(): AuthState {
  if (typeof window === "undefined") return initial;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return initial;
    return normalize(JSON.parse(raw));
  } catch {
    return initial;
  }
}

function persist() {
  if (typeof window === "undefined") return;
  localStorage.setItem(KEY, JSON.stringify(state));
}

function emit() {
  persist();
  listeners.forEach((listener) => listener());
}

function setState(updater: (state: AuthState) => AuthState) {
  state = normalize(updater(state));
  emit();
}

export function subscribeAuth(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useAuth<T>(selector: (state: AuthState) => T): T {
  return useSyncExternalStore(
    subscribeAuth,
    () => selector(state),
    () => selector(initial),
  );
}

export function registerLocalAccount(email: string, password: string): { ok: boolean; message?: string } {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail || !password) {
    return { ok: false, message: "Email dan password wajib diisi" };
  }
  if (state.accounts.some((account) => account.email === normalizedEmail)) {
    return { ok: false, message: "Email sudah terdaftar" };
  }
  const account: LocalAccount = {
    id: uid(),
    email: normalizedEmail,
    password,
    createdAt: new Date().toISOString(),
    employeeStepDone: false,
    onboardingCompleted: false,
  };
  setState((prev) => ({
    ...prev,
    currentUserId: account.id,
    accounts: [...prev.accounts, account],
  }));
  return { ok: true };
}

export function loginLocalAccount(email: string, password: string): { ok: boolean; message?: string } {
  const normalizedEmail = email.trim().toLowerCase();
  const account = state.accounts.find((item) => item.email === normalizedEmail);
  if (!account || account.password !== password) {
    return { ok: false, message: "Email atau password tidak sesuai" };
  }
  setState((prev) => ({ ...prev, currentUserId: account.id }));
  return { ok: true };
}

export function logoutLocalAccount() {
  setState((prev) => ({
    ...prev,
    currentUserId: null,
    accounts: prev.accounts.map((account) =>
      account.id === prev.currentUserId
        ? { ...account, accessToken: undefined, refreshToken: undefined }
        : account,
    ),
  }));
}

/** Access token Supabase untuk header `Authorization` ke API backend. */
export function getAccessToken(): string | null {
  const id = state.currentUserId;
  if (!id) return null;
  const account = state.accounts.find((a) => a.id === id);
  return account?.accessToken ?? null;
}

/** Refresh token untuk memperbarui access token (sesi remote / Supabase). */
export function getRefreshToken(): string | null {
  const id = state.currentUserId;
  if (!id) return null;
  const account = state.accounts.find((a) => a.id === id);
  return account?.refreshToken ?? null;
}

/** Terapkan hasil login atau refresh (token baru disimpan untuk user saat ini). */
export function applyAuthSuccessBody(body: {
  user: { id: string; email: string | null } | null;
  session: { access_token: string; refresh_token: string } | null;
}): void {
  if (!body.user || !body.session) return;
  mergeRemoteSession(body.user, body.session);
}

function mergeRemoteSession(
  user: { id: string; email: string | null },
  session: { access_token: string; refresh_token: string },
) {
  const emailNorm = String(user.email ?? "").trim().toLowerCase();
  setState((prev) => {
    const existing = prev.accounts.find((a) => a.email === emailNorm);
    const others = prev.accounts.filter((a) => a.email !== emailNorm);
    const merged: LocalAccount = {
      id: user.id,
      email: emailNorm,
      password: existing?.password ?? "",
      createdAt: existing?.createdAt ?? new Date().toISOString(),
      employeeStepDone: existing?.employeeStepDone ?? false,
      onboardingCompleted: existing?.onboardingCompleted ?? false,
      authProvider: "remote",
      accessToken: session.access_token,
      refreshToken: session.refresh_token,
    };
    return { currentUserId: user.id, accounts: [...others, merged] };
  });
}

export async function loginWithRemoteApi(email: string, password: string): Promise<{ ok: boolean; message?: string }> {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail || !password) {
    return { ok: false, message: "Email dan password wajib diisi" };
  }
  try {
    const body = await apiLogin(normalizedEmail, password);
    if (!body.user || !body.session) {
      return { ok: false, message: "Login gagal" };
    }
    mergeRemoteSession(body.user, body.session);
    return { ok: true };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : "Login gagal" };
  }
}

export async function registerWithRemoteApi(
  email: string,
  password: string,
): Promise<{ ok: boolean; message?: string; needsEmailConfirmation?: boolean }> {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail || !password) {
    return { ok: false, message: "Email dan password wajib diisi" };
  }
  if (password.length < 8) {
    return { ok: false, message: "Password minimal 8 karakter" };
  }
  try {
    const body = await apiRegister(normalizedEmail, password);
    if (body.user && body.session) {
      mergeRemoteSession(body.user, body.session);
      return { ok: true };
    }
    if (body.user && !body.session) {
      return { ok: true, needsEmailConfirmation: true };
    }
    return { ok: false, message: "Registrasi gagal" };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : "Registrasi gagal" };
  }
}

export function markEmployeeStepDone() {
  setState((prev) => ({
    ...prev,
    accounts: prev.accounts.map((account) =>
      account.id === prev.currentUserId
        ? {
            ...account,
            employeeStepDone: true,
            onboardingCompleted: true,
          }
        : account,
    ),
  }));
}
